import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';

export type ConfirmVariant = 'default' | 'danger';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  variant?: ConfirmVariant;
  onConfirm: () => void | Promise<void>;
  loading?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant = 'default',
  onConfirm,
  loading: loadingProp,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const [internalLoading, setInternalLoading] = useState(false);
  const loading = loadingProp ?? internalLoading;

  const handleConfirm = async () => {
    try {
      setInternalLoading(true);
      await onConfirm();
      onOpenChange(false);
    } finally {
      setInternalLoading(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      e.preventDefault();
      void handleConfirm();
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(v) => !loading && onOpenChange(v)}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink-900/30" />
        <Dialog.Content
          onKeyDown={onKeyDown}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[95vw] max-w-md -translate-x-1/2 -translate-y-1/2',
            'rounded-2xl bg-white border border-ink-200 focus:outline-none',
          )}
        >
          <div className="px-6 py-5">
            <Dialog.Title className="serif-jp text-base font-semibold text-ink-900">
              {title}
            </Dialog.Title>
            {description && (
              <Dialog.Description className="mt-2 text-sm text-ink-600 leading-relaxed">
                {description}
              </Dialog.Description>
            )}
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-ink-200 px-6 py-3">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="text-sm px-3 py-1.5 rounded-md text-ink-700 hover:bg-ink-100 disabled:opacity-50"
            >
              {cancelLabel ?? t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={loading}
              className={cn(
                'text-sm px-3 py-1.5 rounded-md text-white font-medium disabled:opacity-60',
                variant === 'danger'
                  ? 'bg-warn-600 hover:bg-warn-500'
                  : 'bg-accent-500 hover:bg-accent-600',
              )}
            >
              {loading
                ? t('common.loading')
                : confirmLabel ?? t('common.confirm')}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ---------------------------------------------------------------------------
// Imperative API
// ---------------------------------------------------------------------------

interface ImperativeConfirmOpts {
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  variant?: ConfirmVariant;
}

type ConfirmFn = (opts: ImperativeConfirmOpts) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface PendingState extends ImperativeConfirmOpts {
  open: boolean;
  resolve: ((ok: boolean) => void) | null;
}

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PendingState>({
    open: false,
    title: '',
    resolve: null,
  });
  const stateRef = useRef(state);
  stateRef.current = state;

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      setState({ ...opts, open: true, resolve });
    });
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setState((s) => {
        if (s.resolve) s.resolve(false);
        return { ...s, open: false, resolve: null };
      });
    }
  }, []);

  const handleConfirm = useCallback(async () => {
    const cur = stateRef.current;
    if (cur.resolve) cur.resolve(true);
    setState((s) => ({ ...s, open: false, resolve: null }));
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={state.open}
        onOpenChange={handleOpenChange}
        title={state.title}
        description={state.description}
        confirmLabel={state.confirmLabel}
        cancelLabel={state.cancelLabel}
        variant={state.variant}
        onConfirm={handleConfirm}
      />
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback — resolve false if provider missing, never throw.
    return async () => false;
  }
  return ctx;
}
